import { Children, ReactNode, type CSSProperties } from 'react';
import { theme } from '@frontend/theme';
import { createGetSpecificChildren } from '../../helpers';
import { Avatar, AvatarProps } from '../avatar/avatar.component';
import { Text } from '../text/text.component';
import { useTooltip } from '../tooltip';
import {
  avatarStyle,
  firstAvatarStyle,
  groupStyle,
  SIZE_MAP,
  textStyle,
  tooltipContentStyle,
} from './avatar-group.styles';

type AvatarGroupChildrenTypes = 'Avatar';

export type AvatarGroupProps = {
  children: ReactNode;
  size?: AvatarProps['size'];
  maxAvatars?: number;
  borderColor?: string;
  fallbackName: string;
  hideOverflow?: boolean;
};

export const AvatarGroup = ({
  children,
  size = 'medium',
  maxAvatars = 3,
  borderColor,
  fallbackName,
  hideOverflow,
}: AvatarGroupProps) => {
  const getChild = createGetSpecificChildren<AvatarGroupChildrenTypes>(children, true);
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ trigger: 'hover' });
  const avatarChildren = getChild('Avatar');
  const avatarsToDisplay = avatarChildren.slice(0, maxAvatars);
  const overflowAvatars = avatarChildren.slice(maxAvatars);
  const showOverflow = !hideOverflow && overflowAvatars.length > 0;
  const { overlapSpacing, borderWidth, textSize, textSpacing } = SIZE_MAP[size];

  const cssVariables = {
    '--avatar-group-border-color': borderColor || theme.colors.white,
    '--avatar-group-border-width': `${borderWidth}px`,
    '--avatar-group-overlap-spacing': theme.spacing(overlapSpacing),
    '--avatar-group-text-spacing': theme.spacing(textSpacing),
  } as CSSProperties;

  return (
    <>
      <span className={groupStyle} style={cssVariables}>
        {Children.map(avatarsToDisplay, ({ props }, index) => {
          return <Avatar {...props} size={size} className={index === 0 ? firstAvatarStyle : avatarStyle} />;
        })}
        {showOverflow && (
          <Text
            color='light'
            className={textStyle}
            size={textSize}
            {...triggerProps}
          >{`+${overflowAvatars.length}`}</Text>
        )}
      </span>
      {showOverflow && (
        <Tooltip className={tooltipContentStyle} {...tooltipProps}>
          {Children.map(overflowAvatars, (child) => {
            return (
              <span>
                {child}
                <Text color='white'>{child.props.name || fallbackName}</Text>
              </span>
            );
          })}
        </Tooltip>
      )}
    </>
  );
};

AvatarGroup.displayName = 'AvatarGroup';
